/**
 * Wording and formatting for the call rows the chat draws for an internal
 * (Matrix) call. Keep this vocabulary identical in web and MobileCh3 — the
 * two clients render the same conversation, and a call that reads "Missed
 * call" on the phone must not read "Incoming call" on the desktop.
 *
 * Row text is DERIVED, never authored: every surface (timeline bubble, room
 * list preview, notification) builds it through these helpers so the wording
 * cannot fork between producers.
 */

export type CallDirection = 'incoming' | 'outgoing';
export type CallOutcome = 'answered' | 'missed' | 'declined' | 'unknown' | 'ringing';
export type CallRowKind =
    | 'incoming-answered'
    | 'incoming-missed'
    | 'incoming-declined'
    | 'incoming-unknown'
    | 'outgoing-answered'
    | 'outgoing-missed'
    | 'outgoing-declined'
    | 'outgoing-unknown'
    | 'ringing';

/**
 * Titles carry no icon: every surface that prints one draws its own glyph
 * (the timeline bubble an SVG, the room list the missed-call icon).
 */
export const CALL_ROW_TITLES: Record<CallRowKind, string> = {
    'incoming-answered': 'Incoming call',
    'outgoing-answered': 'Outgoing call',
    'incoming-missed': 'Missed call',
    'outgoing-missed': 'Missed outgoing call',
    'incoming-declined': 'Declined call',
    'outgoing-declined': 'Outgoing call',
    'incoming-unknown': 'Call',
    'outgoing-unknown': 'Outgoing call',
    ringing: 'Call',
};

/** Stands where the duration goes for a call that was never picked up. */
export const NOT_ANSWERED_LABEL = 'Not answered';

export const DECLINED_LABEL = 'Declined';

export function callRowKind(direction: CallDirection, outcome: CallOutcome): CallRowKind {
    if (outcome === 'ringing') return 'ringing';
    return `${direction}-${outcome}` as CallRowKind;
}

export function isMissedCallKind(kind: CallRowKind): boolean {
    return kind === 'incoming-missed' || kind === 'outgoing-missed';
}

/**
 * A call someone placed to this user that nobody took — the only kind that
 * owes a call back. The timeline draws both misses red; the room list flags
 * only this one, since a call the user placed themselves asks nothing of them.
 */
export function isMissedIncomingCallKind(kind: CallRowKind): boolean {
    return kind === 'incoming-missed';
}

export function callRowTitle(kind: CallRowKind): string {
    return CALL_ROW_TITLES[kind] ?? CALL_ROW_TITLES.ringing;
}

export function formatCallDurationMs(durationMs: number): string {
    const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const two = (value: number) => String(value).padStart(2, '0');
    return hours > 0
        ? `${hours}:${two(minutes)}:${two(seconds)}`
        : `${minutes}:${two(seconds)}`;
}

function formatCallClockTime(timestampMs: number): string | null {
    if (!Number.isFinite(timestampMs)) return null;
    return new Date(timestampMs).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
}

/**
 * The line under the title: how long the call lasted (or that nobody took
 * it), then when it started — the order the design asks for.
 */
export function callRowDetail(kind: CallRowKind, startedAt: number, durationMs?: number | null): string {
    const lead = isMissedCallKind(kind)
        ? NOT_ANSWERED_LABEL
        : kind === 'outgoing-declined'
            ? DECLINED_LABEL
            : typeof durationMs === 'number' && durationMs > 0
                ? formatCallDurationMs(durationMs)
                : null;
    const time = formatCallClockTime(startedAt);
    return [lead, time].filter(Boolean).join(' · ');
}

/**
 * The room list prints its own timestamp next to the preview, so the preview
 * is the title alone — repeating the hour inside it showed it twice.
 */
export function callPreviewText(kind: CallRowKind): string {
    return callRowTitle(kind);
}

/** Red outcome the timeline card prints in place of a duration (D-CHAT-01/S09). */
export const MISSED_LABEL = 'Missed';

/**
 * Timeline card title (D-CHAT-01/S09): the direction only. How the call ended
 * moves to the detail line, so a missed call reads "Incoming call / Missed".
 * The room list keeps `callRowTitle`, which still says "Missed call".
 */
export function timelineCallTitle(kind: CallRowKind): string {
    if (kind === 'ringing' || kind === 'incoming-unknown') return CALL_ROW_TITLES[kind];
    return kind.startsWith('incoming') ? 'Incoming call' : 'Outgoing call';
}

export interface TimelineCallDetail {
    /** Who placed the call, shown in group rooms only (S10): a name or "You". */
    readonly who: string | null;
    /** Red outcome for a call nobody took, or a declined one. */
    readonly outcome: string | null;
    /** Duration of an answered call. */
    readonly duration: string | null;
    readonly time: string | null;
}

/**
 * The parts of the detail line under the card title, in the order the design
 * prints them: who (groups) · outcome or duration · time.
 */
export function timelineCallDetail(
    kind: CallRowKind,
    startedAt: number,
    durationMs?: number | null,
    who?: string | null,
): TimelineCallDetail {
    const outcome = kind === 'incoming-missed'
        ? MISSED_LABEL
        : kind === 'outgoing-missed'
            ? NOT_ANSWERED_LABEL
            : kind.endsWith('declined')
                ? DECLINED_LABEL
                : null;
    const duration = outcome === null && typeof durationMs === 'number' && durationMs > 0
        ? formatCallDurationMs(durationMs)
        : null;
    return {who: who || null, outcome, duration, time: formatCallClockTime(startedAt)};
}

/** The detail line as one string, for the card's accessible name. */
export function timelineCallDetailText(detail: TimelineCallDetail): string {
    return [detail.who, detail.outcome?.toLowerCase() ?? detail.duration, detail.time].filter(Boolean).join(' · ');
}
